import { useTranslation } from "react-i18next";
import { PERMISSION_AREAS } from "@/hooks/usePermissions";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const LEVELS = ["none", "view", "edit", "manage"] as const;

const SPECIAL_PERMISSIONS = [
  "can_refund_payments",
  "can_cancel_bookings",
  "can_export_data",
  "can_view_revenue",
] as const;

interface RolePermissionMatrixProps {
  permissions: Record<string, string>;
  specialPermissions: Record<string, boolean>;
  onChange: (area: string, level: string) => void;
  onSpecialChange: (key: string, value: boolean) => void;
  disabled?: boolean;
}

export default function RolePermissionMatrix({
  permissions,
  specialPermissions,
  onChange,
  onSpecialChange,
  disabled = false,
}: RolePermissionMatrixProps) {
  const { t } = useTranslation("admin");

  return (
    <div className={cn("space-y-6", disabled && "opacity-50")}>
      {/* Permission matrix table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-medium text-muted-foreground">
                {t("rolesPage.permissionAreas.label", "Område")}
              </th>
              {LEVELS.map((level) => (
                <th
                  key={level}
                  className="px-3 py-2 text-center font-medium text-muted-foreground"
                >
                  {t(`rolesPage.permissionLevels.${level}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_AREAS.map((area) => (
              <tr key={area} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium">
                  {t(`rolesPage.permissionAreas.${area}`)}
                </td>
                {LEVELS.map((level) => (
                  <td key={level} className="px-3 py-2 text-center">
                    <input
                      type="radio"
                      name={`perm-${area}`}
                      value={level}
                      checked={(permissions[area] ?? "none") === level}
                      onChange={() => onChange(area, level)}
                      disabled={disabled}
                      className="h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Special permissions */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          {t("rolesPage.specialPermissions", "Specialbehörigheter")}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SPECIAL_PERMISSIONS.map((key) => (
            <label
              key={key}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={specialPermissions[key] ?? false}
                onChange={(e) => onSpecialChange(key, e.target.checked)}
                disabled={disabled}
                className="h-4 w-4 accent-primary rounded cursor-pointer disabled:cursor-not-allowed"
              />
              <Label className="cursor-pointer font-normal">
                {t(`rolesPage.specialPermissionLabels.${key}`)}
              </Label>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
